import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import Strings from "@my-dld/assets/strings";
import PageTitle from "@my-dld/components/PageTitle";
import { HTRequestsBCItem, MyRequestsBCItem } from "@my-dld/contexts/PageContext";
import PageContent from "@my-dld/components/PageContent";
import { HTActionRequest, HTRequest, HTRequestStatus, HTRequestType, HTRoleType } from "../../../api/entities";
import PersonInfo from "../../components/PersonInfo";
import Status from "../../components/Status";
import Description from "../../components/Description";
import Requirements from "../../components/Requirements";
import Damages from "../../components/Damages";
import ReqDetails from "../../components/requests/ReqDetails";
import Context from "../../../Context";
import FormAction from "@my-dld/components/Form/FormAction";
import FormRow from "@eres/forms-web/FormRow";
import { editPropertyHandTakeOver, showRequestRejection } from "../../../NavigationUtils";
import RejectionInfo from "../../components/RejectionInfo";
import { getDownloadCertificateURL, getRequestDetails, updateAction, validateResponse } from "../../../api/api";
import { setRequest } from "../../../Utils";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { verifyTenantUserProfile } from "@eres/ejari-web/component/TenantContract/PropertyDetails";
import { getParticipantID } from "@10x/sso";
import ReqHistory from "../../components/requests/ReqHistory";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import SuccessPopup from "../../components/SuccessPopup";
import ConfirmationPopup from "@my-dld/components/Popup/ConfirmationPopup";
import { JQModal } from "@my-dld/components/Popup/Utils";
import HandoverDocs from "../../components/HandoverDocs";

interface Props extends RoutedViewProps<{ roleId: string, reqType: string, reqNo: string }> {

}

interface State {
	roleId: number;
	reqType: number;
	reqNo: string;
	tenantId?: number;
	request?: HTRequest;
	confirmActionId?: number;
	actionId?: number;
}

class RequestDetails extends Component<Props, State> {
	static contextType = Context;
	context!: React.ContextType<typeof Context>;

	constructor( props: Props ) {
		super( props );
		const { roleId, reqType, reqNo } = this.props.match.params;
		this.state = {
			roleId: Number( roleId ),
			reqType: Number( reqType ),
			reqNo: reqNo,
		};
	}

	componentDidMount() {
		showLoading();
		if ( this.state.roleId === HTRoleType.TENANT ) {
			this.verifyTenantProfile();
		} else {
			this.loadRequestDetails();
		}
	}

	componentWillUnmount() {
		JQModal( "confirm_cancel" ).hide();
		JQModal( "SuccessPopup" ).hide();
	}

	render() {
		const { roleId, reqType, request, reqNo, actionId, confirmActionId } = this.state;
		return <PageContent title={ this.getPageTitle() } hideQLinks={ true }>
			<PageTitle title={ Strings.request_details } paths={ [ MyRequestsBCItem, HTRequestsBCItem ] }/>
			{ request && <>
				<ReqDetails request={ request }/>
				{ request.RequestStatusId === HTRequestStatus.REJECTED &&
					<RejectionInfo request={ request }/> }
				<PersonInfo mode={ "view" }/>
				<Status mode={ "view" }/>
				<Description mode={ "view" }/>
				{ reqType === HTRequestType.HANDOVER && <>
					<HandoverDocs mode={ "view" }/>
				</> }
				{ reqType === HTRequestType.TAKEOVER && <>
					<Requirements mode={ "view" }/>
					<Damages mode={ "view" }/>
				</> }
				<ReqHistory request={ request }/>
				{ roleId === HTRoleType.OWNER &&
					<RenderOwnerActions
						status={ request.RequestStatusId }
						onModify={ this.onModify }
						onCancel={ this.onCancel }/> }
				{ roleId === HTRoleType.TENANT &&
					<RenderTenantActions
						status={ request.RequestStatusId }
						onAccept={ this.onAccept } onReject={ this.onReject }/> }
				<RenderDownload
					status={ request.RequestStatusId }
					onDownload={ this.onDownload }/>
				{ actionId && <SuccessPopup
					actionId={ actionId }
					reqType={ request.RequestTypeId }
					reqNo={ reqNo } onDismiss={ this.onDismissSuccess }/> }
			</> }
			{ confirmActionId && <ConfirmationPopup
				message={ this.getConfirmMessage() }
				primaryLabel={ this.getPrimaryLabel() }
				onConfirm={ this.onConfirm }
				onCancel={ this.onConfirmDismiss }/> }
		</PageContent>;
	}

	private verifyTenantProfile() {
		verifyTenantUserProfile().then( ( tenantId: number | undefined ) => {
			if ( tenantId && tenantId !== 0 ) {
				this.setState( {
					tenantId: tenantId,
				}, this.loadRequestDetails );
			} else {
				hideLoading();
				showErrorMessage( Strings.tenant_verify_error );
			}
		} ).catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	}

	private loadRequestDetails = () => {
		const { reqNo } = this.state;
		const ownerID = this.getOwnerParticipantId();
		const tenantID = this.getTenantId();
		getRequestDetails( reqNo, ownerID, tenantID ).then( validateResponse ).then( response => {
			setRequest( response );
			this.setState( {
				request: response,
			} );
			hideLoading();
		} ).catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	};

	private getPageTitle() {
		const { reqType, reqNo } = this.state;
		const title = reqType === HTRequestType.HANDOVER ? Strings.ejari_handover : Strings.ejari_takeover;
		return `${ title } - ${ Strings.request_number } : ${ reqNo }`;
	}

	private onModify = () => {
		const { reqType } = this.state;
		editPropertyHandTakeOver( reqType );
	};

	private onAccept = () => {
		this.setState( {
			confirmActionId: HTRequestStatus.ACCEPTED,
		} );
	};

	private onReject = () => {
		showRequestRejection( this.state.reqNo );
	};

	private onDownload = () => {
		showLoading();
		const { request, reqNo } = this.state;
		const ownerID = this.getOwnerParticipantId();
		const tenantID = this.getTenantId();
		addHTAnalytics( "Try", undefined, reqNo, request );
		fetch( getDownloadCertificateURL( reqNo, ownerID, tenantID ) ).then( r => r.blob() ).then( blob => {
			addHTAnalytics( "Success", undefined, reqNo, request );
			const newBlob = new Blob( [ blob ], { type: "application/pdf" } );
			const url = window.URL.createObjectURL( newBlob );
			const a = document.createElement( "a" );
			a.href = url;
			a.download = `${ reqNo }.pdf`;
			a.click();
			hideLoading();
		} ).catch( reason => {
			addHTAnalytics( "Failed", undefined, reqNo, request );
			hideLoading();
			showErrorMessage( reason );
		} );
	};

	private getOwnerParticipantId() {
		const { roleId } = this.state;
		if ( roleId === HTRoleType.OWNER )
			return "" + getParticipantID()!;
		return "";
	}

	private getTenantId() {
		const { roleId, tenantId } = this.state;
		if ( roleId === HTRoleType.TENANT )
			return "" + tenantId;
		return "";
	}

	private onDismissSuccess = () => {
		this.setState( {
			actionId: undefined
		}, () => {
			showLoading();
			this.loadRequestDetails();
		} );
	};

	private onCancel = () => {
		this.setState( {
			confirmActionId: HTRequestStatus.CANCELLED,
		} );
	};

	private onConfirm = () => {
		const { confirmActionId } = this.state;
		if ( confirmActionId === HTRequestStatus.ACCEPTED ) {
			this.submitAccept();
		} else if ( confirmActionId === HTRequestStatus.CANCELLED ) {
			this.submitCancel();
		}
	};

	private onConfirmDismiss = () => {
		this.setState( {
			confirmActionId: undefined,
		} );
	};

	private getConfirmMessage() {
		const { confirmActionId } = this.state;
		if ( confirmActionId === HTRequestStatus.ACCEPTED ) {
			return Strings.disclaimer_tenant_accept;
		} else if ( confirmActionId === HTRequestStatus.CANCELLED ) {
			return Strings.alert_cancel;
		}
		return "";
	};

	private getPrimaryLabel() {
		const { confirmActionId } = this.state;
		if ( confirmActionId === HTRequestStatus.ACCEPTED ) {
			return Strings.agree;
		}
		return Strings.yes;
	};

	private submitAccept() {
		showLoading();
		const { request, reqNo } = this.state;
		const req: HTActionRequest = {
			ActionByTenantId: this.state.tenantId,
			RequestStatusId: HTRequestStatus.ACCEPTED,
			RequestId: request?.Identity,
		};
		addHTAnalytics( "Try", HTRequestStatus.ACCEPTED, reqNo, request );
		updateAction( req ).then( validateResponse ).then( resp => {
			addHTAnalytics( "Success", HTRequestStatus.ACCEPTED, reqNo, request );
			hideLoading();
			this.setState( {
				actionId: HTRequestStatus.ACCEPTED,
				reqNo: resp.RequestNumber,
			} );
		} ).catch( reason => {
			addHTAnalytics( "Failed", HTRequestStatus.ACCEPTED, reqNo, request );
			hideLoading();
			showErrorMessage( reason );
		} );
	}

	private submitCancel() {
		showLoading();
		const { request, reqNo } = this.state;
		const req: HTActionRequest = {
			ActionByPartId: getParticipantID(),
			RequestStatusId: HTRequestStatus.CANCELLED,
			RequestId: request?.Identity,
		};
		addHTAnalytics( "Try", HTRequestStatus.CANCELLED, reqNo, request );
		updateAction( req ).then( validateResponse ).then( resp => {
			addHTAnalytics( "Success", HTRequestStatus.CANCELLED, reqNo, request );
			hideLoading();
			this.setState( {
				actionId: HTRequestStatus.CANCELLED,
				reqNo: resp.RequestNumber,
			} );
		} ).catch( reason => {
			addHTAnalytics( "Failed", HTRequestStatus.CANCELLED, reqNo, request );
			hideLoading();
			showErrorMessage( reason );
		} );
	}
}

function RenderOwnerActions( props: { status: number, onModify: () => void, onCancel: () => void } ) {
	const { status, onModify, onCancel } = props;
	if ( status === HTRequestStatus.SUBMITTED ) {
		return <FormRow>
			<FormAction
				actionContainerClassName={ "col-md-12" }
				primaryActionLabel={ Strings.cancel_request }
				onPrimaryAction={ onCancel }/>
		</FormRow>;
	} else if ( status === HTRequestStatus.DRAFT || status === HTRequestStatus.REJECTED ) {
		return <FormRow>
			<FormAction
				actionContainerClassName={ "col-md-12" }
				primaryActionLabel={ Strings.modify }
				onPrimaryAction={ onModify }
				secondaryActionLabel={ Strings.cancel_request }
				onSecondaryAction={ onCancel }/>
		</FormRow>;
	}
	return null;
}

function RenderTenantActions( props: { status: number, onAccept: () => void, onReject: () => void } ) {
	const { status, onAccept, onReject } = props;
	if ( status === HTRequestStatus.SUBMITTED ) {
		return <FormRow>
			<FormAction
				actionContainerClassName={ "col-md-12" }
				primaryActionLabel={ Strings.accept }
				onPrimaryAction={ onAccept }
				secondaryActionLabel={ Strings.reject }
				onSecondaryAction={ onReject }/>
		</FormRow>;
	}
	return null;
}

function RenderDownload( props: { status: number, onDownload: () => void } ) {
	const { status, onDownload } = props;
	if ( status === HTRequestStatus.ACCEPTED ) {
		return <FormRow>
			<FormAction
				actionContainerClassName={ "col-md-12" }
				primaryActionLabel={ Strings.download_certificate }
				onPrimaryAction={ onDownload }/>
		</FormRow>;
	}
	return null;
}

export function addHTAnalytics( actionType: string, actionId?: number, reqNo?: string, request?: HTRequest ) {
	const category = ( request?.RequestTypeId === HTRequestType.HANDOVER ) ? "Handover" : "Takeover";
	let action = "Download";
	if ( actionId === HTRequestStatus.ACCEPTED ) {
		action = "Accept";
	} else if ( actionId === HTRequestStatus.REJECTED ) {
		action = "Reject";
	} else if ( actionId === HTRequestStatus.CANCELLED ) {
		action = "Cancel";
	}
	let contractNo = request?.Properties[ 0 ].ContractNumber;
	let data: any = { contractNo: contractNo };
	if ( reqNo ) {
		data = { ...data, reqNo: reqNo };
	}
	trackEventWithData( category, `${ action }${ actionType }`, data );
}

export default RequestDetails;
